import styled from '@emotion/styled'
import { Popper, PopperProps } from '@mui/material'
import { MouseEvent, useEffect, useRef, useState } from 'react'

import useLoggedInUser from '@/hooks/useLoggedInUser'

const POPOVER_CLOSE_DELAY_MS = 80
const POPPER_ESTIMATED_WIDTH = 340
const VIEWPORT_PADDING = 16

/** Keep poppers below/above the anchor; never flip to left/right beside the planogram. */
const POPPER_FALLBACK_PLACEMENTS = [
  'bottom-start',
  'bottom-end',
  'top-start',
  'top-end',
] as const

export const getProductPopperPlacement = (
  anchorEl: HTMLElement | null,
): PopperProps['placement'] => {
  if (!anchorEl) return 'bottom-start'

  const { left, right } = anchorEl.getBoundingClientRect()
  const viewportWidth = window.innerWidth

  const wouldOverflowRight =
    left + POPPER_ESTIMATED_WIDTH + VIEWPORT_PADDING > viewportWidth

  if (wouldOverflowRight) return 'bottom-end'

  const wouldOverflowLeft =
    right - POPPER_ESTIMATED_WIDTH - VIEWPORT_PADDING < 0

  if (wouldOverflowLeft) return 'bottom-start'

  return 'bottom-start'
}

export const getProductPopperModifiers = (
  arrowRef: Element | null,
): NonNullable<PopperProps['modifiers']> => [
  {
    name: 'arrow',
    enabled: true,
    options: {
      element: arrowRef,
    },
  },
  {
    name: 'offset',
    options: {
      offset: [0, 12],
    },
  },
  {
    name: 'shiftIntoViewport',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['popperOffsets'],
    fn({ state }) {
      const padding = VIEWPORT_PADDING
      const popperWidth = state.rects.popper.width
      const maxX = window.innerWidth - popperWidth - padding
      const minX = padding
      const popperOffsets = state.modifiersData.popperOffsets

      if (!popperOffsets) return

      if (popperOffsets.x > maxX) {
        popperOffsets.x = maxX
      }

      if (popperOffsets.x < minX) {
        popperOffsets.x = minX
      }
    },
  },
  {
    name: 'preventOverflow',
    options: {
      boundary: 'viewport',
      rootBoundary: 'viewport',
      altAxis: true,
      tether: false,
      padding: VIEWPORT_PADDING,
    },
  },
  {
    name: 'flip',
    options: {
      boundary: 'viewport',
      padding: VIEWPORT_PADDING,
      fallbackPlacements: [...POPPER_FALLBACK_PLACEMENTS],
    },
  },
]

export const productPopperProps = {
  popperOptions: {
    strategy: 'fixed',
  },
} satisfies Pick<PopperProps, 'popperOptions'>

export const productPopoverContentSx = {
  zIndex: 100,
  width: POPPER_ESTIMATED_WIDTH,
  maxWidth: POPPER_ESTIMATED_WIDTH,
  pointerEvents: 'auto',
  '& .MuiCard-root': {
    minWidth: 'unset',
    width: '100%',
  },
} as const

export const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1,
  maxWidth: '375px',
  width: 'max-content',
  pointerEvents: 'none',
  '&[data-popper-placement*="bottom"] .arrow': {
    top: 0,
    left: 0,
    marginTop: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
    },
  },
  '&[data-popper-placement*="top"] .arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
    },
  },
  '&[data-popper-placement*="right"] .arrow': {
    left: 0,
    marginLeft: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
    },
  },
  '&[data-popper-placement*="left"] .arrow': {
    right: 0,
    marginRight: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
    },
  },
}))

export const useProductPopover = () => {
  const styles = {
    arrow: {
      position: 'absolute',
      fontSize: 7,
      width: '3em',
      height: '3em',
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: 0,
        height: 0,
        borderStyle: 'solid',
      },
    },
  }
  const { user } = useLoggedInUser()

  const [activeUpc, setActiveUpc] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [arrowRef, setArrowRef] = useState(null)

  const open = Boolean(activeUpc) && Boolean(anchorEl)

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setActiveUpc(null)
      setAnchorEl(null)
    }, POPOVER_CLOSE_DELAY_MS)
  }

  useEffect(() => () => clearCloseTimeout(), [])

  const handleBoxClick = (event: MouseEvent<HTMLElement>, upc: string) => {
    if (user?.isSuperuser) {
      // used to allow for quick updates to planogram products or other product details
      window.open(
        `https://admin.deliciousai.com/Products?upc=${upc}&searchTerm=${upc}`,
        '_blank',
      )
    }
    clearCloseTimeout()
    setAnchorEl(event.currentTarget)
    setActiveUpc(upc)
  }

  const handleBoxMouseEnter = (event: MouseEvent<HTMLElement>, upc: string) => {
    clearCloseTimeout()
    setAnchorEl(event.currentTarget)
    setActiveUpc(upc)
  }

  const handleBoxMouseLeave = () => {
    scheduleClose()
  }

  const handlePopperMouseEnter = () => {
    clearCloseTimeout()
  }

  const handlePopperMouseLeave = () => {
    scheduleClose()
  }

  return {
    open,
    anchorEl,
    arrowRef,
    handleBoxClick,
    handleBoxMouseEnter,
    handleBoxMouseLeave,
    handlePopperMouseEnter,
    handlePopperMouseLeave,
    activeUpc,
    styles,
    setArrowRef,
  }
}
